import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { CalendarDays, Home as HomeIcon, ShoppingBag, User, Sparkles, CalendarPlus } from "lucide-react";

const TIMES = ["9:00","9:30","10:00","10:30","11:00","11:30","1:00","1:30","2:00","2:30","3:00","3:30","4:00"];

const nav = [
  { to: "/portal", label: "Dashboard", end: true, icon: HomeIcon },
  { to: "/portal/book-iv", label: "Book an IV", icon: CalendarPlus },
  { to: "/portal/appointments", label: "Upcoming", icon: CalendarDays },
  { to: "/portal/membership", label: "Membership", icon: Sparkles },
  { to: "/portal/orders", label: "Order History", icon: ShoppingBag },
  { to: "/portal/account", label: "Account", icon: User },
];

function Sidebar() {
  return (
    <aside className="lg:sticky lg:top-24">
      <div className="rounded-3xl bg-white border border-sand-200/60 p-3">
        {nav.map(n=>(
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`portal-nav-${n.label.replace(/\s+/g,'-').toLowerCase()}`} className={({isActive})=>`flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm ${isActive?"bg-sage-700 text-white":"text-sage-800 hover:bg-sand-100"}`}>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </div>
    </aside>
  );
}

function Dashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  useEffect(() => { api.get("/iv-appointments/mine").then(({data})=>setAppts(data)).catch(()=>{}); }, []);
  const next = appts.find(a=>a.status==="confirmed") || appts[0];
  return (
    <div data-testid="portal-dashboard">
      <div className="overline">Welcome back</div>
      <h1 className="font-display text-4xl text-sage-800 mt-2">Hi, {user?.name?.split(' ')[0]}.</h1>
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <div className="rounded-3xl bg-sage-700 text-white p-6">
          <div className="overline text-clay-400">Your membership</div>
          <div className="font-display text-2xl mt-3">{user?.membership_tier || "No plan yet"}</div>
          <div className="text-sand-100/75 text-sm mt-2">{user?.membership_tier ? "Active" : "Upgrade to unlock IV member perks."}</div>
          <Button asChild className="btn-pill h-10 mt-5 bg-white text-sage-800 hover:bg-sand-100"><Link to="/memberships">Explore memberships</Link></Button>
        </div>
        <div className="rounded-3xl bg-white border border-sand-200/60 p-6">
          <div className="overline">Next appointment</div>
          {next ? (
            <>
              <div className="font-display text-xl text-sage-800 mt-3">{next.iv_type} · {next.date}</div>
              <div className="text-sm text-sage-800/70 mt-1">{next.time} — <span className="capitalize">{next.status}</span></div>
            </>
          ) : <div className="text-sm text-sage-800/60 mt-3">Nothing scheduled yet.</div>}
          <Button asChild className="btn-pill h-10 mt-5 bg-sage-700 hover:bg-sage-800 text-white"><Link to="/portal/book-iv">Book an IV</Link></Button>
        </div>
        <div className="rounded-3xl bg-sand-100 border border-sand-200/60 p-6">
          <div className="overline">Shop</div>
          <div className="font-display text-xl text-sage-800 mt-3">Members save on every order.</div>
          <Button asChild variant="outline" className="btn-pill h-10 mt-5 border-sage-700/30 text-sage-800"><Link to="/shop">Browse the shop</Link></Button>
        </div>
      </div>
    </div>
  );
}

function BookIV() {
  const [types, setTypes] = useState([]);
  const [iv, setIv] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/iv-types").then(({data})=>setTypes(data)); }, []);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/iv-appointments/request", { iv_type: iv, date: format(date, "yyyy-MM-dd"), time, notes });
      setDone(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Please try again."); }
    finally { setLoading(false); }
  };
  if (done) return (
    <div data-testid="iv-booking-success">
      <h1 className="font-display text-3xl text-sage-800">Appointment Requested</h1>
      <p className="mt-3 text-sage-800/70">We're holding your requested time while the Restore Wellness team reviews. You'll receive confirmation shortly.</p>
      <Button asChild className="btn-pill h-11 mt-6 bg-sage-700 hover:bg-sage-800 text-white"><Link to="/portal/appointments">View appointments</Link></Button>
    </div>
  );
  return (
    <div data-testid="portal-book-iv">
      <div className="overline">Members only</div>
      <h1 className="font-display text-4xl text-sage-800 mt-2">Book your next IV</h1>
      <div className="mt-8 space-y-8">
        <div>
          <div className="text-sm text-sage-800/60 mb-2">Choose an IV blend</div>
          <Select value={iv} onValueChange={setIv}>
            <SelectTrigger className="max-w-md rounded-xl h-12" data-testid="iv-type-select"><SelectValue placeholder="Select an IV" /></SelectTrigger>
            <SelectContent>{types.map(t=>(<SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        {iv && (
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <div className="text-sm text-sage-800/60 mb-2">Pick a date</div>
              <div className="rounded-3xl bg-white border border-sand-200/70 p-3 w-fit">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} data-testid="iv-calendar" />
              </div>
            </div>
            <div>
              <div className="text-sm text-sage-800/60 mb-2">Available times</div>
              <div className="grid grid-cols-3 gap-2">
                {TIMES.map(t=>(
                  <button key={t} data-testid={`iv-time-${t}`} onClick={()=>setTime(t)} disabled={!date} className={`rounded-full h-10 text-sm border ${time===t?"bg-sage-700 text-white border-sage-700":"bg-white border-sand-300 text-sage-800 hover:bg-sand-100 disabled:opacity-40"}`}>{t}</button>
                ))}
              </div>
              {date && time && (
                <div className="mt-6">
                  <Label>Notes for provider (optional)</Label>
                  <Textarea rows={3} value={notes} onChange={(e)=>setNotes(e.target.value)} data-testid="iv-notes" />
                  <Button onClick={submit} disabled={loading} className="btn-pill h-11 mt-4 bg-sage-700 hover:bg-sage-800 text-white px-7" data-testid="iv-submit">{loading?"Requesting…":"Request Appointment"}</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Appointments() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/iv-appointments/mine").then(({data})=>setItems(data)); }, []);
  return (
    <div data-testid="portal-appointments">
      <h1 className="font-display text-4xl text-sage-800">Upcoming Appointments</h1>
      <div className="mt-8 space-y-3">
        {items.length===0 && <div className="text-sage-800/60">Nothing scheduled yet.</div>}
        {items.map(a=>(
          <div key={a.id} className="rounded-2xl bg-white border border-sand-200/60 p-5 flex items-center justify-between gap-4" data-testid={`appt-${a.id}`}>
            <div>
              <div className="font-display text-lg text-sage-800">{a.iv_type}</div>
              <div className="text-sm text-sage-800/70">{a.date} · {a.time}</div>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full uppercase tracking-wider ${a.status==="confirmed"?"bg-sage-700 text-white":a.status==="declined"||a.status==="cancelled"?"bg-destructive/10 text-destructive":"bg-sand-200 text-sage-800"}`}>{a.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Membership() {
  const { user } = useAuth();
  return (
    <div data-testid="portal-membership">
      <h1 className="font-display text-4xl text-sage-800">Your Membership</h1>
      <div className="mt-6 rounded-3xl bg-white border border-sand-200/60 p-7">
        <div className="overline">Current plan</div>
        <div className="font-display text-2xl mt-2 text-sage-800">{user?.membership_tier || "Not enrolled"}</div>
        <div className="text-sm text-sage-800/60 mt-2">{user?.membership_tier ? "Renews monthly. Cancel anytime." : "You're not currently on a plan — explore options to unlock member perks."}</div>
        <Button asChild className="btn-pill h-11 mt-6 bg-sage-700 hover:bg-sage-800 text-white"><Link to="/memberships">{user?.membership_tier?"Manage plan":"Explore memberships"}</Link></Button>
      </div>
    </div>
  );
}

function Orders() {
  return (
    <div data-testid="portal-orders">
      <h1 className="font-display text-4xl text-sage-800">Order History</h1>
      <div className="mt-6 rounded-3xl bg-sand-100 border border-sand-200/60 p-8 text-sage-800/70">
        Your Shopify order history will appear here once the shop is live.
      </div>
    </div>
  );
}

function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div data-testid="portal-account">
      <h1 className="font-display text-4xl text-sage-800">Account</h1>
      <div className="mt-6 rounded-3xl bg-white border border-sand-200/60 p-7 max-w-lg">
        <div className="space-y-3 text-sm">
          <div><span className="text-sage-800/60">Name</span><div className="text-sage-800">{user?.name}</div></div>
          <div><span className="text-sage-800/60">Email</span><div className="text-sage-800">{user?.email}</div></div>
          <div><span className="text-sage-800/60">Phone</span><div className="text-sage-800">{user?.phone || "—"}</div></div>
        </div>
        <Button variant="outline" className="btn-pill h-10 mt-6 border-sage-700/30 text-sage-800" data-testid="account-signout" onClick={async ()=>{ await logout(); navigate("/"); }}>Sign out</Button>
      </div>
    </div>
  );
}

export default function MemberPortal() {
  return (
    <div className="section-y" data-testid="member-portal">
      <div className="container-x grid lg:grid-cols-[240px_1fr] gap-10">
        <Sidebar />
        <main>
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="book-iv" element={<BookIV />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="membership" element={<Membership />} />
            <Route path="orders" element={<Orders />} />
            <Route path="account" element={<Account />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
